/**
 * Estados de Cuenta — migración web de ares7010 (escritorio).
 *
 * Reutiliza el endpoint existente /api/expediente_detalle (la vista 360 del
 * expediente): resumen del financiamiento (deuda total, pagado, balance) y
 * un bloque "Exigible a la fecha" calculado EN EL FRONT con las cuotas de la
 * amortización ya vencidas: pendiente por cuota = capital + interés −
 * vpagcap − vpagint − descinte, más la mora teórica de cada cuota.
 *
 * OJO: la fórmula de mora del escritorio estaba ROTA — convivían dos
 * versiones inconsistentes del cálculo. Aquí se usa una sola:
 * mora diaria = pendiente × (mora% / 100 / 30) × días de atraso.
 * El % de mora del préstamo no viene en expediente_detalle, así que se toma
 * de /api/ares4030_crediticio (moraprestamo), también existente.
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const money = (value) => fmtMoney(value);

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Las fechas del backend vienen "dd-mm-yyyy".
const parseFecha = (s) => {
  const [d, m, y] = String(s || "")
    .split(/[-/]/)
    .map((x) => parseInt(x, 10));
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
};

const hoyTexto = () => {
  const h = new Date();
  const dd = String(h.getDate()).padStart(2, "0");
  const mm = String(h.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${h.getFullYear()}`;
};

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const chipInfo = { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 };

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 130,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Estado de cuenta imprimible: compañía + cliente + financiamiento +
// exigible a la fecha + pendiente total.
function imprimirEstadoCuenta(compania, r, exigible) {
  const filasVencidas = exigible.cuotas
    .map(
      (c) => `<tr>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${c.nocuota}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${c.fecha}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${c.dias}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          c.pendiente
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          c.moateorica
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          c.pendiente + c.moateorica
        )}</td>
      </tr>`
    )
    .join("");
  const vehiculo = [r.marca, r.modelo, r.colore].filter(Boolean).join(" ");
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Estado de Cuenta ${esc(r.noprest)}</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:12px">
      <h3 style="text-align:center;margin:0 0 2px">${esc(compania?.nombre || "")}</h3>
      <p style="text-align:center;margin:0">${esc(compania?.direccion || "")}</p>
      <p style="text-align:center;margin:0 0 10px">${
        compania?.rnc ? `RNC: ${esc(compania.rnc)}` : ""
      }</p>
      <p style="text-align:center;margin:0 0 14px;font-weight:bold;font-size:14px">
        Estado de Cuenta — Préstamo #${esc(r.noprest)} — al ${hoyTexto()}
      </p>
      <table style="width:100%;font-size:12px;margin-bottom:12px">
        <tr><td><b>Cliente:</b> ${esc(r.nombres)}</td><td><b>Cédula:</b> ${esc(r.cedula)}</td></tr>
        <tr><td><b>Dirección:</b> ${esc(
          [r.direccion, r.urb].filter(Boolean).join(", ")
        )}</td><td><b>Teléfono:</b> ${esc(
    [r.telefono, r.celular].filter(Boolean).join(" · ")
  )}</td></tr>
        <tr><td><b>Vehículo:</b> ${esc(vehiculo) || "—"}</td><td><b>Chasis:</b> ${
    esc(r.chasis) || "—"
  }</td></tr>
        <tr><td><b>Fecha:</b> ${esc(r.fecha)}</td><td><b>Plazo:</b> ${esc(r.plazo)} ${esc(
    r.periodos
  )}</td></tr>
        <tr><td><b>Deuda total:</b> RD$ ${fmtMoney(r.deuda)}</td>
            <td><b>Pagado:</b> RD$ ${fmtMoney(r.pagado)}</td></tr>
        <tr><td><b>Último pago:</b> ${esc(r.fultpago) || "—"} (RD$ ${fmtMoney(
    r.montoult
  )})</td><td><b>Balance pendiente:</b> RD$ ${fmtMoney(r.balance)}</td></tr>${
    toNum(r.completivopendiente) > 0
      ? `<tr><td colspan="2"><b>Completivo del inicial pendiente:</b> RD$ ${fmtMoney(
          r.completivopendiente
        )} (vencido RD$ ${fmtMoney(r.completivovencido)})</td></tr>`
      : ""
  }
      </table>
      <p style="margin:0 0 6px;font-weight:bold">Exigible a la fecha (${
        exigible.cuotas.length
      } cuotas vencidas · mora ${fmtMoney(exigible.moraPct)} %)</p>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <thead><tr style="background:#eee">
          <th style="padding:4px 6px;text-align:left">Cuota</th>
          <th style="padding:4px 6px;text-align:left">Vencía</th>
          <th style="padding:4px 6px;text-align:right">Días</th>
          <th style="padding:4px 6px;text-align:right">Pendiente</th>
          <th style="padding:4px 6px;text-align:right">Mora teórica</th>
          <th style="padding:4px 6px;text-align:right">Total</th>
        </tr></thead>
        <tbody>${
          filasVencidas ||
          `<tr><td colspan="6" style="padding:6px;text-align:center">Sin cuotas vencidas</td></tr>`
        }</tbody>
      </table>
      <p style="text-align:right;font-weight:bold;border-top:1px solid #000;padding-top:6px;margin-top:10px">
        Exigible: RD$ ${fmtMoney(exigible.monto)} ·
        Mora teórica: RD$ ${fmtMoney(exigible.mora)} ·${
          exigible.completivo > 0
            ? ` Completivo vencido: RD$ ${fmtMoney(exigible.completivo)} ·`
            : ""
        }
        Total exigible: RD$ ${fmtMoney(exigible.total)}
      </p>
      <p style="text-align:right;font-weight:bold">
        Pendiente total del préstamo: RD$ ${fmtMoney(r.balance)}${
          toNum(r.completivopendiente) > 0
            ? ` · Completivo del inicial: RD$ ${fmtMoney(r.completivopendiente)}`
            : ""
        }
      </p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function EstadosDeCuenta() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [prestamos, setPrestamos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [prestamo, setPrestamo] = useState(null); // opción del autocomplete
  const [detalle, setDetalle] = useState(null); // respuesta de expediente_detalle
  const [moraPct, setMoraPct] = useState(0); // % de mora (ares4030_crediticio)
  const [buscando, setBuscando] = useState(false);
  const [compania, setCompania] = useState(null);

  useEffect(() => {
    let activo = true;
    setCargando(true);
    post("/api/refinanciamiento_prestamos", {})
      .then(({ ok, data }) => {
        if (!activo) return;
        if (ok) setPrestamos(data.prestamos || []);
        else
          showAlert({
            message: data.Error || "No se pudieron cargar los préstamos",
            color: "error",
          });
      })
      .finally(() => activo && setCargando(false));
    post("/api/cobropago_perfil", {}).then(({ ok, data }) => {
      if (activo && ok) setCompania(data.compania || null);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiar = () => {
    setPrestamo(null);
    setDetalle(null);
    setMoraPct(0);
  };

  const elegirPrestamo = async (p) => {
    setPrestamo(p);
    setDetalle(null);
    setMoraPct(0);
    if (!p) return;
    setBuscando(true);
    try {
      const [exp, cred] = await Promise.all([
        post("/api/expediente_detalle", { noprest: p.noprest }),
        post("/api/ares4030_crediticio", { noprest: p.noprest }),
      ]);
      if (!exp.ok) {
        showAlert({
          message: exp.data.Error || "No se pudo recuperar el estado de cuenta",
          color: "error",
        });
        setPrestamo(null);
        return;
      }
      setDetalle(exp.data);
      setMoraPct(cred.ok ? toNum(cred.data.prestamo?.moraprestamo) : 0);
    } finally {
      setBuscando(false);
    }
  };

  const r = detalle?.resumen || null;

  // Exigible a la fecha: cuotas de la amortización ya vencidas con su
  // pendiente real (capital + interés − pagos − descuento) y la mora
  // teórica de cada una: pendiente × (mora%/100/30) × días de atraso.
  const exigible = useMemo(() => {
    if (!detalle) return null;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const cuotas = [];
    let monto = 0;
    let mora = 0;
    (detalle.amortizacion || []).forEach((a) => {
      const f = parseFecha(a.fecha);
      if (!f || f >= hoy) return;
      const pendiente = Math.max(
        0,
        toNum(a.capital) +
          toNum(a.interes) -
          toNum(a.vpagcap) -
          toNum(a.vpagint) -
          toNum(a.descinte)
      );
      if (pendiente <= 0.005) return;
      const dias = Math.floor((hoy - f) / 86400000);
      const moateorica = pendiente * (moraPct / 100 / 30) * dias;
      monto += pendiente;
      mora += moateorica;
      cuotas.push({
        id: a.nocuota,
        nocuota: a.nocuota,
        fecha: a.fecha,
        dias,
        pendiente,
        moateorica,
      });
    });
    // Completivo del inicial vencido (lo que el cobro propone hoy según su
    // plan): se exige aparte de las cuotas, antes que ellas.
    const completivo = toNum(detalle.resumen?.completivovencido);
    return { cuotas, monto, mora, completivo, total: monto + mora + completivo, moraPct };
  }, [detalle, moraPct]);

  const columnsVencidas = [
    { field: "nocuota", headerName: "Cuota", width: 90 },
    { field: "fecha", headerName: "Vencía", width: 120 },
    {
      field: "dias",
      headerName: "Días de atraso",
      width: 130,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "pendiente",
      headerName: "Pendiente",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "moateorica",
      headerName: "Mora teórica",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "total",
      headerName: "Total",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueGetter: (_v, row) => row.pendiente + row.moateorica,
      valueFormatter: money,
    },
  ];

  const imprimir = () => {
    if (!r || !exigible) return;
    imprimirEstadoCuenta(compania, r, exigible);
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Estados de Cuenta"
        subtitle="Situación del financiamiento y exigible a la fecha (ares7010)"
        cta={
          <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
            Nuevo
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Préstamo */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.accentBlue}>
            Préstamo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={prestamos}
                loading={cargando || buscando}
                value={prestamo}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => elegirPrestamo(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest} — {o.nombres}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.cedula, o.chasis, o.sucursal].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Préstamo"
                    helperText="Busca por número, nombre, cédula o chasis"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            {r && (
              <Grid item xs={12} md={7} sx={{ display: "flex", alignItems: "center" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={r.nombres} sx={chipInfo} />
                  <Chip label={`Cédula ${r.cedula || "—"}`} sx={chipInfo} />
                  {(r.marca || r.modelo) && (
                    <Chip
                      label={[r.marca, r.modelo, r.colore].filter(Boolean).join(" · ")}
                      sx={chipInfo}
                    />
                  )}
                  {r.chasis && <Chip label={`Chasis ${r.chasis}`} sx={chipInfo} />}
                  <Chip label={`Fecha ${r.fecha || "—"}`} sx={chipInfo} />
                  <Chip label={`Plazo ${r.plazo} ${r.periodos || ""}`.trim()} sx={chipInfo} />
                  <Chip label={`Mora ${fmtMoney(moraPct)} %`} sx={chipInfo} />
                  <Chip label={`Sucursal ${r.sucursal || "—"}`} sx={chipInfo} />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Resumen del financiamiento */}
        <Box sx={sectionBox}>
          <SectionTitle icon={AccountBalanceOutlinedIcon} color={FP.color.success}>
            Financiamiento
          </SectionTitle>
          {!r ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ py: 6, color: FP.color.textMuted, bgcolor: "#fff", borderRadius: 2 }}
            >
              {buscando ? (
                <CircularProgress size={28} />
              ) : (
                <>
                  <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
                    Seleccione un préstamo para ver su estado de cuenta
                  </Typography>
                </>
              )}
            </Stack>
          ) : (
            <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
              <ResumenChip label="DEUDA TOTAL" value={`RD$ ${fmtMoney(r.deuda)}`} />
              <ResumenChip
                label="PAGADO"
                value={`RD$ ${fmtMoney(r.pagado)}`}
                color={FP.color.success}
              />
              <ResumenChip label="BALANCE" value={`RD$ ${fmtMoney(r.balance)}`} color="#E65100" />
              {toNum(r.completivopendiente) > 0 && (
                <ResumenChip
                  label="COMPLETIVO DEL INICIAL"
                  value={`RD$ ${fmtMoney(r.completivopendiente)}`}
                  color="#E65100"
                />
              )}
              <ResumenChip
                label="ÚLTIMO PAGO"
                value={r.fultpago ? `${r.fultpago} · RD$ ${fmtMoney(r.montoult)}` : "—"}
              />
            </Stack>
          )}
        </Box>

        {/* Exigible a la fecha */}
        {r && exigible && (
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <SectionTitle icon={EventBusyOutlinedIcon} color={FP.color.danger}>
              Exigible a la Fecha
            </SectionTitle>
            <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
              <ResumenChip
                label="CUOTAS VENCIDAS"
                value={String(exigible.cuotas.length)}
                color={exigible.cuotas.length ? FP.color.danger : FP.color.navy}
              />
              <ResumenChip
                label="MONTO EXIGIBLE"
                value={`RD$ ${fmtMoney(exigible.monto)}`}
                color={exigible.monto ? FP.color.danger : FP.color.navy}
              />
              <ResumenChip
                label="MORA TEÓRICA"
                value={`RD$ ${fmtMoney(exigible.mora)}`}
                color={exigible.mora ? FP.color.danger : FP.color.navy}
              />
              {exigible.completivo > 0 && (
                <ResumenChip
                  label="COMPLETIVO VENCIDO"
                  value={`RD$ ${fmtMoney(exigible.completivo)}`}
                  color={FP.color.danger}
                />
              )}
              <ResumenChip
                label="TOTAL EXIGIBLE"
                value={`RD$ ${fmtMoney(exigible.total)}`}
                color={exigible.total ? FP.color.danger : FP.color.navy}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                startIcon={<PrintOutlinedIcon />}
                onClick={imprimir}
                sx={btnOutline(FP.color.navy)}
              >
                Imprimir Estado de Cuenta
              </Button>
            </Stack>
            {exigible.cuotas.length > 0 ? (
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={exigible.cuotas}
                  columns={columnsVencidas}
                  rowHeight={42}
                  hideFooter={exigible.cuotas.length <= 25}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  sx={fpDataGridSx}
                  disableRowSelectionOnClick
                />
              </Box>
            ) : (
              <Typography sx={{ fontSize: "0.85rem", color: FP.color.textMuted }}>
                El préstamo está al día — no hay cuotas vencidas.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
